import { Search, User as UserIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { TextInput, SwitchInput } from '@/components/atoms'
import { AddressModel, CustomerModel as CustomerModelType } from '@/types'
import { useFetchCustomerList } from '@/hooks/features/customers'
import { useEnterSubmit } from '@/hooks/ui'
import { cn } from '@/lib/utils'

import { Label } from '@/components/ui/label'
import { AddressFormState, addressFieldsConfig } from './addressFieldsConfig'
import { Spinner } from '@/components/ui/spinner'

interface AddressDialogProps {
    open: boolean
    mode: 'create' | 'edit' | 'view'
    editAddress: AddressModel.Address | null
    closeDialog: () => void
    onCreate: (data: AddressFormState) => void
    onUpdate: (id: string, data: AddressFormState) => void
    loading?: boolean
    defaultUserId?: string
}

export const AddressDialog = ({
    open,
    mode,
    editAddress,
    closeDialog,
    onCreate,
    onUpdate,
    loading = false,
    defaultUserId,
}: AddressDialogProps) => {
    const {
        handleSubmit,
        reset,
        formState: { errors },
        control,
        watch,
        setValue,
    } = useForm<AddressFormState>({
        defaultValues: {
            customer_id: '',
            name: '',
            phone: '',
            address: '',
            status: 'active',
        },
    })

    const formValues = watch()
    const { fields } = addressFieldsConfig({ formValues })
    const [customerOpen, setCustomerOpen] = useState(false)
    const [customerSearch, setCustomerSearch] = useState('')
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerModelType.Customer | null>(null)

    const { customers, loading: customersLoading } = useFetchCustomerList({
        search: customerSearch,
        status: 'active',
        pageSize: 100,
        autoFetch: open && !defaultUserId, // Only auto fetch list if we don't have a fixed user
    })

    // Pre-select customer logic
    useEffect(() => {
        if (open) {
            if (defaultUserId) {
                // If we have a default user ID, we should try to find them in the list or use their ID directly
                // In a real scenario, we might need to fetch the specific user by ID if not in list
                // For now, we set the ID and wait for list to populate selectedCustomer for label
                setValue('customer_id', defaultUserId)
                const customer = customers.find((c) => c._id === defaultUserId)
                if (customer) {
                    setSelectedCustomer(customer)
                    if (mode === 'create') {
                        if (!formValues.name) setValue('name', `${customer.name} (Shipping)`)
                        if (!formValues.phone) setValue('phone', customer.phone || '')
                    }
                }
            }
        }
    }, [open, defaultUserId, customers, setValue, mode])

    useEffect(() => {
        if (open) {
            if ((mode === 'edit' || mode === 'view') && editAddress) {
                reset({
                    customer_id: editAddress.customer_id,
                    name: editAddress.name,
                    phone: editAddress.phone,
                    address: editAddress.address,
                    status: editAddress.status,
                })
                const customer = customers.find((c) => c._id === editAddress.customer_id)
                if (customer) {
                    setSelectedCustomer(customer)
                }
            } else if (!defaultUserId) {
                reset({
                    customer_id: '',
                    name: '',
                    phone: '',
                    address: '',
                    status: 'active',
                })
                setSelectedCustomer(null)
            }
        }
    }, [open, mode, editAddress, reset, customers, defaultUserId])

    const onSubmit = async (data: AddressFormState) => {
        if (mode === 'create') {
            onCreate(data)
        } else {
            if (!editAddress?._id) return
            onUpdate(editAddress._id, data)
        }
    }

    useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

    const handleCustomerSelect = (customer: CustomerModelType.Customer) => {
        setSelectedCustomer(customer)
        setValue('customer_id', customer._id)
        setValue('name', customer.name)
        setValue('phone', customer.phone || '')
        setCustomerOpen(false)
    }

    return (
        <Dialog open={open} onOpenChange={closeDialog}>
            <DialogContent size="md">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create'
                            ? 'Add address'
                            : mode === 'edit'
                                ? 'Edit address'
                                : 'Address details'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Add a delivery address for this customer.'
                            : mode === 'edit'
                                ? 'Update the customer\'s delivery address.'
                                : 'Review this delivery address.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Customer Selection */}
                    <div className="space-y-1.5">
                        <Label className="text-sm text-ink">Customer</Label>
                        <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={customerOpen}
                                    className={cn(
                                        'h-8 w-full justify-between rounded-md px-3 text-sm font-normal',
                                        !selectedCustomer && 'text-muted-foreground',
                                        errors.customer_id && 'border-error'
                                    )}
                                    disabled={mode === 'view' || mode === 'edit' || !!defaultUserId}>
                                    {selectedCustomer ? (
                                        <span className="flex min-w-0 items-center gap-2">
                                            <UserIcon className="text-text-2" />
                                            <span className="truncate">
                                                {selectedCustomer.name}{' '}
                                                <span className="text-muted-foreground">({selectedCustomer.email})</span>
                                            </span>
                                        </span>
                                    ) : (
                                        <span>
                                            {defaultUserId ? 'Loading customer…' : 'Select a customer'}
                                        </span>
                                    )}
                                    <Search className="text-muted-foreground" />
                                </Button>
                            </PopoverTrigger>
                            {!defaultUserId && (
                                <PopoverContent
                                    className="w-[var(--radix-popover-trigger-width)] p-0"
                                    align="start"
                                >
                                    <Command>
                                        <CommandInput
                                            placeholder="Search customers"
                                            onValueChange={setCustomerSearch}
                                        />
                                        <CommandList className="max-h-[300px]">
                                            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                                                {customersLoading ? 'Loading customers…' : 'No customers found.'}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {customers.map((customer) => (
                                                    <CommandItem
                                                        key={customer._id}
                                                        value={`${customer.name} ${customer.email}`}
                                                        onSelect={() => handleCustomerSelect(customer)}
                                                        className="flex cursor-pointer items-center gap-3"
                                                    >
                                                        <UserIcon className="size-4 text-text-2" />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate text-sm font-medium text-ink">{customer.name}</p>
                                                            <p className="truncate text-xs text-muted-foreground">
                                                                {customer.email}
                                                            </p>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            )}
                        </Popover>
                        {errors.customer_id && (
                            <p className="text-sm text-error">
                                {errors.customer_id.message}
                            </p>
                        )}
                    </div>

                    <TextInput
                        control={control}
                        name="name"
                        label="Address name"
                        placeholder="e.g. John Doe (Home)"
                        disabled={mode === 'view'}
                        rules={fields.name.rules}
                        errors={errors}
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextInput
                            control={control}
                            name="phone"
                            label="Phone"
                            placeholder="01877665544"
                            disabled={mode === 'view'}
                            rules={fields.phone.rules}
                            errors={errors}
                        />

                        <div className="space-y-1.5">
                            <Label className="text-sm text-ink">Status</Label>
                            <SwitchInput
                                control={control}
                                name="status"
                                label=""
                                description={
                                    formValues?.status === 'active' ? 'Active' : 'Inactive'
                                }
                                disabled={mode === 'view'}
                                activeValue="active"
                                inactiveValue="inactive"
                                rules={fields.status.rules}
                            />
                        </div>
                    </div>

                    <TextInput
                        control={control}
                        name="address"
                        label="Address"
                        placeholder="House/flat no., street, area, city"
                        disabled={mode === 'view'}
                        multiline
                        rows={4}
                        rules={fields.address.rules}
                        errors={errors}
                    />
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={closeDialog}
                        disabled={loading}>
                        {mode === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {mode !== 'view' && (
                        <Button
                            type="button"
                            disabled={loading}
                            onClick={handleSubmit(onSubmit)}>
                            {loading && <Spinner />}
                            {mode === 'create' ? 'Save address' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
